import { AppointmentDraft } from 'pages/Appointments/state/appointmentsModalSlice';
import { useCallback } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  AppointmentCreateInput,
  GetPaginatedAppointmentsDocument,
  useCreateOneClinicPetMutation,
  useCreateOneClinicPetParentMutation,
  useUpdateOneAppointmentMutation,
  useUpdateOneClinicPetMutation,
  useUpdateOneClinicPetParentMutation,
} from 'shared/types/graphql';
import { nonStandardAppointmentNoClientName, nonStandardAppointmentNoPatientName } from '../constants';

type UseSaveNonStandardClinicAppointmentReturnType = {
  createAppointment: (appointmentDraft: AppointmentDraft) => Promise<void>;
  updateAppointment: (
    appointmentId: string,
    appointmentDraft: AppointmentDraft,
    originalAppointment: AppointmentDraft,
  ) => Promise<void>;
  isLoading: boolean;
};

const useSaveNonStandardClinicAppointment = (): UseSaveNonStandardClinicAppointmentReturnType => {
  const { currentClinicId } = useClinicUser();

  const [createPetParentMutation, { loading: isCreatingPetParent }] = useCreateOneClinicPetParentMutation({
    refetchQueries: [GetPaginatedAppointmentsDocument],
  });

  const [createPetMutation, { loading: isCreatingPet }] = useCreateOneClinicPetMutation({
    refetchQueries: [GetPaginatedAppointmentsDocument],
  });

  const [updatePetParentMutation, { loading: isUpdatingPetParent }] = useUpdateOneClinicPetParentMutation({
    refetchQueries: [GetPaginatedAppointmentsDocument],
  });

  const [updatePetMutation, { loading: isUpdatingPet }] = useUpdateOneClinicPetMutation({
    refetchQueries: [GetPaginatedAppointmentsDocument],
  });

  const [updateAppointmentMutation, { loading: isUpdatingAppointment }] = useUpdateOneAppointmentMutation({
    refetchQueries: [GetPaginatedAppointmentsDocument],
  });

  const isLoading =
    isCreatingPetParent || isCreatingPet || isUpdatingPetParent || isUpdatingPet || isUpdatingAppointment;

  // Handle scenarios where we need to create or update pet and pet parent
  const createAppointment = useCallback(
    async (appointmentDraft: AppointmentDraft) => {
      const { startAt, durationInMinutes, petParentName, petName } = appointmentDraft;

      const appointmentData: AppointmentCreateInput = {
        description: '',
        clinic: { connect: { id: currentClinicId } },
        startAt,
        durationInMinutes,
      };

      const createPetParentResult = await createPetParentMutation({
        variables: {
          data: {
            firstName: petParentName || nonStandardAppointmentNoClientName,
            lastName: '',
            clinic: { connect: { id: currentClinicId } },
          },
        },
      });
      const newPetParentId = createPetParentResult.data?.createOneClinicPetParent?.id;

      // Create the appointment through the pet creation
      await createPetMutation({
        variables: {
          data: {
            name: petName || nonStandardAppointmentNoPatientName,
            clinic: { connect: { id: currentClinicId } },
            petParents: { connect: [{ id: newPetParentId }] },
            appointments: {
              create: [{ ...appointmentData, clinicPetParents: { connect: [{ id: newPetParentId }] } }],
            },
          },
        },
      });
    },
    [createPetMutation, createPetParentMutation, currentClinicId],
  );

  const updateAppointment = useCallback(
    async (appointmentId: string, appointmentDraft: AppointmentDraft, originalAppointment: AppointmentDraft) => {
      if (!appointmentId) return;

      const { startAt, durationInMinutes, petParentIds, petParentName, petId, petName } = appointmentDraft;

      const petParentId = petParentIds?.[0];

      if (petParentName !== originalAppointment.petParentName) {
        await updatePetParentMutation({
          variables: {
            where: { id: petParentId },
            data: { firstName: petParentName || nonStandardAppointmentNoClientName, lastName: '' },
          },
        });
      }

      if (petName !== originalAppointment.petName) {
        await updatePetMutation({
          variables: {
            where: { id: petId },
            data: { name: petName || nonStandardAppointmentNoPatientName },
          },
        });
      }

      await updateAppointmentMutation({
        variables: {
          where: { id: appointmentId },
          data: {
            startAt,
            durationInMinutes,
          },
        },
      });
    },
    [updateAppointmentMutation, updatePetMutation, updatePetParentMutation],
  );

  return {
    updateAppointment,
    createAppointment,
    isLoading,
  };
};

export default useSaveNonStandardClinicAppointment;
